import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { RoleApi } from '../../services/ApiService';

function RoleList() {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadRoles();
  }, []);

  const loadRoles = async () => {
    setLoading(true);
    setError(null);
    try {
      console.log('RoleList: Making API call to get roles...');
      const response = await RoleApi.getRoles();
      console.log('RoleList: API response status:', response.status);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('RoleList: API error response:', errorText);
        throw new Error('Failed to load roles');
      }
      
      const data = await response.json();
      console.log('RoleList: Raw API response data:', data);
      console.log('RoleList: Data structure:', {
        isArray: Array.isArray(data),
        firstItem: data[0],
        keys: data[0] ? Object.keys(data[0]) : 'no items'
      });
      
      setRoles(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('RoleList: Error loading roles:', error);
      setError('Failed to load roles. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id, roleName) => {
    if (!window.confirm(`Are you sure you want to delete the role "${roleName}"? This action cannot be undone and will affect all users with this role.`)) {
      return;
    }

    try {
      const response = await RoleApi.deleteRole(id);
      if (response.ok) {
        loadRoles();
      } else {
        throw new Error('Failed to delete role');
      }
    } catch (error) {
      console.error('Error deleting role:', error);
      setError('Failed to delete role. Please try again.');
    }
  };

  const getRoleTypeColor = (roleName) => {
    const roleColors = {
      'admin': '#dc3545',
      'moderator': '#fd7e14',
      'instructor': '#6f42c1',
      'student': '#20c997',
      'user': '#6c757d'
    };
    
    const lowerRoleName = roleName?.toLowerCase();
    for (const [role, color] of Object.entries(roleColors)) {
      if (lowerRoleName?.includes(role)) {
        return color;
      }
    }
    return '#6c757d';
  };

  const filteredRoles = roles.filter(role =>
    role.roleName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    role.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="settings-container">
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Loading roles...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="settings-page-content">
      <div className="settings-page-header">
        <h2>User Roles</h2>
        <Link to="/settings/roles/create">
          <button type="button">Create New Role</button>
        </Link>
      </div>
      
      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      <div className="search-container">
        <input
          type="text"
          placeholder="Search roles..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      {filteredRoles.length === 0 ? (
        <div className="empty-state">
          <h3>No roles found</h3>
          <p>{searchTerm ? 'No roles match your search.' : 'No roles found. Create your first one!'}</p>
        </div>
      ) : (
        <table>
          <thead>
            <tr>
              <th>Role Name</th>
              <th>Description</th>
              <th>Users</th>
              <th>Created</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredRoles.map((role) => (
              <tr key={role.roleId}>
                <td data-label="Role Name">
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <span 
                      style={{
                        padding: '0.25rem 0.5rem',
                        backgroundColor: getRoleTypeColor(role.roleName),
                        color: 'white',
                        borderRadius: '12px',
                        fontSize: '0.75rem',
                        fontWeight: '600',
                        textTransform: 'uppercase',
                        letterSpacing: '0.025em'
                      }}
                    >
                      {role.roleName || 'Unnamed Role'}
                    </span>
                  </div>
                </td>
                <td data-label="Description">{role.description || 'No description'}</td>
                <td data-label="Users">
                  <span style={{
                    color: '#6c757d',
                    fontWeight: '500'
                  }}>
                    {role.userCount || 0} users
                  </span>
                </td>
                <td data-label="Created">
                  {role.createdAt ? new Date(role.createdAt).toLocaleDateString() : 'Unknown'}
                </td>
                <td data-label="Actions">
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <Link to={`/settings/roles/${role.roleId}`}>
                      Edit
                    </Link>
                    <Link to={`/settings/roles/${role.roleId}/permissions`}>
                      Permissions
                    </Link>
                    {role.roleName?.toLowerCase() !== 'admin' && (
                      <button onClick={() => handleDelete(role.roleId, role.roleName)}>
                        Delete
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <div className="info-panel">
        <h4>About User Roles</h4>
        <p>
          User roles define what permissions and access levels users have within the system. Roles help you control 
          who can view, edit, or delete different types of content. Common roles include Admin (full system access), 
          Instructor (can manage courses and students), and Student (can access learning materials).
        </p>
      </div>
    </div>
  );
}

export default RoleList;